import type { EntryPoint, EntryPointComponent } from 'react-relay';

import { RovoAgentAri } from '@atlassian/ari';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import agentProfileCardQueryParameters from './__generated__/AgentProfileCardContainerQuery.graphql.ts';
import type { AgentProfileCardQueries } from './AgentProfileCardContainer.tsx';
import type { AgentProfileCardRuntimeProps } from './types.ts';

export type AgentProfileCardEntryPointParams =
	| { agentAri: string }
	| { agentId: string; activationId: string }
	| { identityAccountId: string; cloudId: string };

type ResolvedQueryVariables = {
	agentAri: string;
	identityAccountId: string;
	cloudId: string;
	fetchByIdentity: boolean;
};

function resolveQueryVariables(params: AgentProfileCardEntryPointParams): ResolvedQueryVariables {
	if ('identityAccountId' in params) {
		const identityAccountId = params.identityAccountId.startsWith('ari:cloud:identity::user/')
			? params.identityAccountId
			: `ari:cloud:identity::user/${params.identityAccountId}`;
		return {
			agentAri: '',
			identityAccountId,
			cloudId: params.cloudId,
			fetchByIdentity: true,
		};
	}
	const agentAri =
		'agentAri' in params
			? params.agentAri
			: RovoAgentAri.create({
					activationId: params.activationId,
					agentId: params.agentId,
				}).toString();
	return { agentAri, identityAccountId: '', cloudId: '', fetchByIdentity: false };
}

type AgentProfileCardEntryPointComponent = EntryPointComponent<
	AgentProfileCardQueries,
	Record<string, never>,
	AgentProfileCardRuntimeProps,
	Record<string, never>
>;

export const agentProfileCardEntryPoint: EntryPoint<
	AgentProfileCardEntryPointComponent,
	AgentProfileCardEntryPointParams
> = createEntryPoint({
	root: JSResourceForInteraction<AgentProfileCardEntryPointComponent>(
		/* webpackChunkName: "@atlassian-internal_conversation-assistant-agent_agent-profile-card" */ './AgentProfileCardContainer.tsx',
		{
			moduleId: 'conversation-assistant-agent_agent-profile-card',
		},
	),
	getPreloadProps: (params: AgentProfileCardEntryPointParams) => {
		const queryVariables = resolveQueryVariables(params);

		return {
			queries: {
				agentProfileCardQuery: {
					parameters: agentProfileCardQueryParameters,
					variables: queryVariables,
					options: { fetchPolicy: 'store-or-network' as const },
				},
			},
		};
	},
});
